import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';

import { createSubmitHandlers } from '@/components/auth/AccountFinder/handlers';
import { useAccountFinder } from '@/components/auth/AccountFinder/hooks';
import { YourAccountForm } from '@/components/forms';
import YourAccountFormV2 from '@/components/forms/YourAccountFormV2';
import {
  useUserRegistrationContext,
  USER_REGISTRATION_JOURNEY_KEY_OPTIONS,
} from '@/context/UserRegistrationContext';
import { useAccountIdentification } from '@/hooks/auth/useAccountIdentification';
import { useUserRegistrationStatus } from '@/hooks/auth/useUserRegistrationStatus';
import { useVerifyIdentity } from '@/hooks/auth/useVerifyIdentity';
import { useVerifyIdentityFallback } from '@/hooks/auth/useVerifyIdentityFallback';
import { useSyncFeatureFlag } from '@/hooks/utils/useFeatureFlags';
import { FeatureNames } from '@/types/features';
import { userRegistrationJourneyConfig } from '@/utils/identifyAndVerify';
import { INTERNAL_PATHS } from '@/utils/urls';

const AccountFinder = ({
  journeyKey,
}: {
  journeyKey: USER_REGISTRATION_JOURNEY_KEY_OPTIONS;
}) => {
  const { push } = useRouter();
  const queryClient = useQueryClient();
  const { isResetting, hasInitialized, isTokenSet, scopedToken } =
    useAccountFinder();

  const {
    mutate: mutateVerifyIdentity,
    isSuccess: isVerifyIdentitySuccess,
    isError: isVerifyIdentityError,
  } = useVerifyIdentity();

  const {
    mutate: mutateVerifyIdentityFallback,
    isSuccess: isVerifyIdentityFallbackSuccess,
    isError: isVerifyIdentityFallbackError,
  } = useVerifyIdentityFallback();

  const isRegisterV2Enabled = useSyncFeatureFlag(
    FeatureNames.RegisterForOnlineAccountV2
  );

  const {
    accountNumber,
    postcode,
    surname,
    billingAddress,
    setAccountFields,
    setSelectedAccountUserName,
    setScopedToken,
    setHasDateOfBirth,
    setHasMobileNumber,
    setBlackholeAccountUsers,
    setSelectedUserId,
  } = useUserRegistrationContext();

  const { handleSubmit, handleSubmitV2 } = createSubmitHandlers({
    queryClient,
    setAccountFields,
    setScopedToken,
    mutateVerifyIdentity,
    mutateVerifyIdentityFallback,
  });

  // Query for account identification data
  const {
    data: accountIdentificationData,
    isSuccess: isAccountIdentificationSuccess,
  } = useAccountIdentification({
    familyName: surname,
    scopedToken,
  });

  // Query for user registration status
  const { data: userRegistrationStatus, isSuccess: userRegistrationSuccess } =
    useUserRegistrationStatus({
      scopedToken:
        userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails &&
        isAccountIdentificationSuccess
          ? scopedToken
          : '',
    });

  // Display an alert if the user has already registered an email, but only on the registration journey
  const displayEmailInUseAlert =
    userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails &&
    !!userRegistrationStatus?.email;

  const saveBlackholeAccountDataToContext = useCallback(() => {
    // Save whatever security info we have for this user to context, so we can use it on later pages. Not only do we need some of this on the security page for cases where only one user is returned, but this means we don't need to call the blackhole email account API again.
    setHasDateOfBirth(
      accountIdentificationData.blackholeEmailAccountUser.hasDateOfBirth
    );
    setHasMobileNumber(
      accountIdentificationData.blackholeEmailAccountUser.hasMobileNumber
    );
    setBlackholeAccountUsers(
      accountIdentificationData.blackholeEmailAccountUser.accounts[0]
        .accountUserNames
    );
  }, [
    accountIdentificationData,
    setHasDateOfBirth,
    setHasMobileNumber,
    setBlackholeAccountUsers,
  ]);

  useEffect(() => {
    // Skip redirection if component is still initializing or resetting
    if (isResetting || !hasInitialized) return;

    // Check if all conditions for redirection are met
    if (
      (isVerifyIdentitySuccess || isVerifyIdentityFallbackSuccess) &&
      isTokenSet &&
      (userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails
        ? userRegistrationSuccess && userRegistrationStatus
        : isAccountIdentificationSuccess)
    ) {
      if (accountIdentificationData) {
        const notEnoughInfoToProceed =
          !accountIdentificationData.blackholeEmailAccountUser.hasDateOfBirth &&
          !accountIdentificationData.blackholeEmailAccountUser.hasMobileNumber;

        // Redirect to next step of the journey
        if (
          accountIdentificationData.blackholeEmailAccountUser.accounts[0]
            .accountUserNames.length === 1
        ) {
          // If the user has already registered an email, or if the user doesn't have either a DOB or mobile number, don't do a redirect
          if (displayEmailInUseAlert || notEnoughInfoToProceed) {
            return;
          }

          // Save the blackhole account data to context, now that we know we're able to proceed
          saveBlackholeAccountDataToContext();

          const accountUser =
            accountIdentificationData.blackholeEmailAccountUser.accounts[0]
              .accountUserNames[0];

          // If there is only one matching user, go straight to the security page, after saving this user's details for later use
          setSelectedAccountUserName({
            givenName: accountUser.givenName,
            familyName: accountUser.familyName,
          });
          setSelectedUserId(accountUser.id);

          // Go straight to security page, since we don't need to select a user
          push(INTERNAL_PATHS[`${journeyKey}_SECURITY`].path);
        } else {
          // Save the blackhole account data to context
          saveBlackholeAccountDataToContext();
          push(INTERNAL_PATHS[`${journeyKey}_ABOUT_YOU`].path);
        }
      }
    }
  }, [
    isVerifyIdentitySuccess,
    isVerifyIdentityFallbackSuccess,
    setSelectedAccountUserName,
    push,
    isAccountIdentificationSuccess,
    accountIdentificationData,
    displayEmailInUseAlert,
    saveBlackholeAccountDataToContext,
    journeyKey,
    setSelectedUserId,
    userRegistrationSuccess,
    userRegistrationStatus,
    scopedToken,
    isResetting,
    hasInitialized,
    isTokenSet,
  ]);

  return (
    <>
      {isRegisterV2Enabled &&
      journeyKey ===
        USER_REGISTRATION_JOURNEY_KEY_OPTIONS.REGISTER_FOR_ONLINE_ACCOUNT ? (
        <YourAccountFormV2
          postcode={postcode}
          billingAddress={billingAddress}
          surname={surname}
          onSubmit={handleSubmitV2}
          showGenericError={isVerifyIdentityFallbackError}
          journeyKey={journeyKey}
        />
      ) : (
        <YourAccountForm
          accountNumber={accountNumber}
          postcode={postcode}
          surname={surname}
          billingAddress={billingAddress}
          onSubmit={handleSubmit}
          showGenericError={isVerifyIdentityError}
          journeyKey={journeyKey}
        />
      )}
    </>
  );
};

export default AccountFinder;
